import { Badge } from "@/components/badge";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/chart";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/collapsible";
import { useExperimentVertexRuns } from "@/http/api-hooks";
import { VertexRun, VertexRunBucket, VertexRunState } from "@/http/api-types";
import { cn, formatUTCDateTime } from "@/lib/utils";
import { formatChartDayLabel } from "@/pages/experiments/diagnostics";
import { buildVertexRunsChartData, groupRunsByModel, vertexRunStateColor } from "@/pages/experiments/vertex-runs";
import { ChevronRight, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

type VertexRunsPanelProps = {
  experimentId: number | null;
};

const STATE_STYLES: Record<string, string> = {
  FAILED: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200",
  CANCELLED: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
  RUNNING: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200",
  SUCCEEDED: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
};

function stateBadgeClass(state: VertexRunState): string {
  return STATE_STYLES[state] ?? "bg-muted text-muted-foreground";
}

function formatTimestamp(value: string | undefined): string {
  if (!value) return "—";
  return formatUTCDateTime(value);
}

function runEndOrCreate(run: VertexRun): string {
  return run.end_time ? formatTimestamp(run.end_time) : formatTimestamp(run.create_time);
}

type RunGroupProps = {
  title: string;
  description: string;
  buckets: VertexRunBucket[];
  emptyNote: string;
};

function RunGroup({ title, description, buckets, emptyNote }: RunGroupProps) {
  const { rows: chartRows, states: chartStates } = useMemo(() => buildVertexRunsChartData(buckets), [buckets]);
  const modelGroups = useMemo(() => groupRunsByModel(buckets), [buckets]);
  const totalRuns = useMemo(() => buckets.reduce((sum, bucket) => sum + bucket.runs.length, 0), [buckets]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const config: ChartConfig = {};
    for (const state of chartStates) {
      config[state] = { label: state, color: vertexRunStateColor(state) };
    }
    return config;
  }, [chartStates]);

  return (
    <div>
      <h5 className="text-sm font-semibold tracking-tight">{title}</h5>
      <p className="mb-3 text-sm text-muted-foreground">{description}</p>

      {buckets.length === 0 ? (
        <div className="text-sm text-muted-foreground">{emptyNote}</div>
      ) : (
        <div className="space-y-4">
          {chartRows.length > 0 && (
            <ChartContainer config={chartConfig} className="aspect-auto h-[240px] w-full">
              <BarChart data={chartRows} margin={{ top: 8, right: 12, left: 12, bottom: 8 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={24}
                  tickFormatter={formatChartDayLabel}
                />
                <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                <ChartTooltip
                  content={({ active, label, payload }) => (
                    <ChartTooltipContent
                      active={active}
                      label={label}
                      payload={payload?.filter(item => Number(item.value) > 0)}
                      labelFormatter={value => formatChartDayLabel(String(value))}
                    />
                  )}
                />
                {chartStates.map(state => (
                  <Bar key={state} dataKey={state} stackId="runs" fill={vertexRunStateColor(state)} />
                ))}
              </BarChart>
            </ChartContainer>
          )}

          <Collapsible>
            <CollapsibleTrigger className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground transition-colors hover:text-foreground/80 [&[data-state=open]>svg]:rotate-90">
              <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
              Show runs
              <span className="text-xs font-normal text-muted-foreground">({totalRuns})</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-4">
              {modelGroups.map(group => (
                <div key={group.model}>
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{group.model}</span>
                    <span className="text-xs text-muted-foreground">{group.runs.length}</span>
                  </div>
                  <ul className="space-y-1">
                    {group.runs.map(run => (
                      <li
                        key={run.job_name}
                        className="flex items-center justify-between gap-3 rounded-md border border-border/70 px-3 py-2 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge className={cn("font-medium", stateBadgeClass(run.state))}>{run.state}</Badge>
                            <a
                              href={run.console_url}
                              target="_blank"
                              rel="noreferrer"
                              className="truncate font-medium text-primary hover:underline"
                            >
                              {run.display_name}
                            </a>
                            {run.is_migration ? (
                              <Badge
                                variant="outline"
                                className="shrink-0 border-purple-300 text-purple-700 dark:border-purple-800 dark:text-purple-300"
                              >
                                migration
                              </Badge>
                            ) : null}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {run.version} · {run.location}
                            {run.schedule_url ? (
                              <>
                                {" · "}
                                <a href={run.schedule_url} target="_blank" rel="noreferrer" className="hover:underline">
                                  schedule
                                </a>
                              </>
                            ) : null}
                          </div>
                        </div>
                        <span className="shrink-0 text-xs text-muted-foreground">{runEndOrCreate(run)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  );
}

export function VertexRunsPanel({ experimentId }: VertexRunsPanelProps) {
  const { data, isLoading, error } = useExperimentVertexRuns(experimentId);
  const test = data?.test ?? [];
  const control = data?.control ?? [];
  const hasAny = test.length > 0 || control.length > 0;

  return (
    <div>
      <h4 className="text-base font-semibold tracking-tight">Vertex AI runs</h4>
      <p className="mb-3 text-sm text-muted-foreground">
        Pipeline runs during this experiment&apos;s window, grouped by state. Each run links to the Vertex AI console.
      </p>

      {isLoading ? (
        <div className="flex h-24 items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-sm text-muted-foreground">Failed to load Vertex AI runs.</div>
      ) : !hasAny ? (
        <div className="text-sm text-muted-foreground">
          No Vertex AI runs found within this experiment&apos;s window. Custom experiments need at least one repo/branch
          pair configured; global experiments derive them from the prediction-flights config.
        </div>
      ) : (
        <div className="space-y-6">
          <RunGroup
            title="Test runs"
            description="Runs of this experiment's configured repo/branch pairs."
            buckets={test}
            emptyNote="No test runs found within this experiment's window."
          />
          <RunGroup
            title="Control runs (current release)"
            description="Release runs of the same repos during the experiment's window — the production baseline the test is compared against."
            buckets={control}
            emptyNote="No release runs found for these repos within this experiment's window."
          />
        </div>
      )}
    </div>
  );
}
